import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useAuth } from "../../../auth/AuthContext";
import AxionControls from "../../../components/controls/AxionControls";
import {
  createWorkOrderComment,
  deleteWorkOrderComment,
  getWorkOrderComments,
  updateWorkOrderComment,
} from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import { formatTimestamp } from "../../cmms/cmmsFormat";
import { CmmsListSkeleton, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import { getAssetHierarchyPalette, getContainedButtonSx } from "../../cmms/assetHierarchyStyles";

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map((part) => part[0]).slice(0, 2).join("").toUpperCase();
};

export default function WorkOrderCommentList({ workOrderId, companyKey, userNameById }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const saveFailed = intl.formatMessage(messages.commentsSaveFailed);
  const { user } = useAuth();
  const currentUserId = user?.id;

  const [newBody, setNewBody] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  const commentsQuery = useQuery(
    ["cmmsWorkOrderComments", companyKey, workOrderId],
    () => getWorkOrderComments(companyKey, workOrderId),
    { enabled: Boolean(companyKey && workOrderId), staleTime: 15000 },
  );

  // None of these send an author id. The API signs the comment with the token's user and checks
  // that same user against the comment before an edit or a delete; currentUserId below is only
  // for deciding which comments show the edit and delete buttons.
  const createMutation = useMutation({
    mutationFn: (body) => createWorkOrderComment(workOrderId, { companyKey, body }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ commentId, body }) =>
      updateWorkOrderComment(workOrderId, commentId, { companyKey, body }),
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId) => deleteWorkOrderComment(companyKey, workOrderId, commentId),
  });

  const comments = (commentsQuery.data ?? [])
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const handleSubmitNew = async () => {
    if (!newBody.trim()) return;
    try {
      await createMutation.mutateAsync(newBody.trim());
      setNewBody("");
      pushNotification(intl.formatMessage(messages.toastCommentAdded));
      await commentsQuery.refetch();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, saveFailed));
    }
  };

  const handleStartEdit = (comment) => {
    setEditingId(comment.id);
    setEditBody(comment.body);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditBody("");
  };

  const handleSubmitEdit = async () => {
    if (!editBody.trim() || !editingId) return;
    try {
      await updateMutation.mutateAsync({ commentId: editingId, body: editBody.trim() });
      pushNotification(intl.formatMessage(messages.toastCommentUpdated));
      handleCancelEdit();
      await commentsQuery.refetch();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, saveFailed));
    }
  };

  const handleDelete = (comment) => {
    setConfirmation({
      title: intl.formatMessage(messages.commentsDeleteTitle),
      message: intl.formatMessage(messages.commentsDeleteMessage),
      confirmText: intl.formatMessage(cmmsMessages.delete),
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(comment.id);
          setConfirmation(null);
          pushNotification(intl.formatMessage(messages.toastCommentDeleted));
          await commentsQuery.refetch();
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, saveFailed));
        }
      },
    });
  };

  // react-query v4 exposes isLoading on mutations; isPending is the v5 name.
  const isMutating = createMutation.isPending || createMutation.isLoading
    || updateMutation.isPending || updateMutation.isLoading
    || deleteMutation.isPending || deleteMutation.isLoading;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        <FormattedMessage {...messages.commentsCount} values={{ count: comments.length }} />
      </Typography>

      {commentsQuery.isLoading &&
        <CmmsListSkeleton rows={3} height={28} />
      }
      {commentsQuery.error &&
        <CmmsErrorState error={commentsQuery.error} fallback={saveFailed} onRetry={() => commentsQuery.refetch()} isRetrying={commentsQuery.isFetching} dense />
      }
      {!commentsQuery.isLoading && !commentsQuery.error && comments.length === 0 &&
        <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", mb: 2 }}>
          <FormattedMessage {...messages.commentsEmpty} />
        </Typography>
      }

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {comments.map((comment) => {
          const authorName = userNameById?.get(comment.authorUserId) ?? `#${comment.authorUserId}`;
          const isOwn = comment.authorUserId === currentUserId;
          const isEditing = editingId === comment.id;
          return (
            <Box key={comment.id} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{getInitials(authorName)}</Avatar>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{authorName}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {formatTimestamp(intl, comment.createdAt)}
                    {comment.updatedAt && comment.updatedAt !== comment.createdAt ? ` ${intl.formatMessage(messages.commentEdited)}` : ""}
                  </Typography>
                </Stack>
                {!isEditing &&
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.25 }}>
                    {comment.body}
                  </Typography>
                }
                {isEditing &&
                  <Box sx={{ mt: 0.5 }}>
                    <TextField
                      fullWidth
                      multiline
                      size="small"
                      value={editBody}
                      onChange={(event) => setEditBody(event.target.value)}
                    />
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      <Button size="small" variant="contained" onClick={handleSubmitEdit} disabled={isMutating} sx={getContainedButtonSx(cmmsColors)}>
                        <FormattedMessage {...cmmsMessages.save} />
                      </Button>
                      <Button size="small" onClick={handleCancelEdit}><FormattedMessage {...cmmsMessages.cancel} /></Button>
                    </Stack>
                  </Box>
                }
              </Box>
              {isOwn && !isEditing &&
                <Box>
                  <IconButton size="small" aria-label={intl.formatMessage(messages.commentsEditAria)} onClick={() => handleStartEdit(comment)}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton size="small" aria-label={intl.formatMessage(messages.commentsDeleteAria)} onClick={() => handleDelete(comment)}>
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              }
            </Box>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          fullWidth
          multiline
          size="small"
          placeholder={intl.formatMessage(messages.commentsPlaceholder)}
          value={newBody}
          onChange={(event) => setNewBody(event.target.value)}
        />
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSubmitNew}
          disabled={!newBody.trim() || isMutating}
          sx={getContainedButtonSx(cmmsColors)}
        >
          Post
        </Button>
      </Stack>

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={isMutating} />
    </Box>
  );
}
